<template>
  <AttrGroup
    title="填充"
    :show-add="maxCount > 1"
    :tooltip="maxCount > 1 ? '支持多个视频或序列帧' : ''"
    @add="onAdd"
  >
    <!-- 选择类型 -->
    <AttrSelect
      v-if="canUseVideo && shape.type == ShapeType.Sprite"
      class="mb-2.5"
      :value="shape.attrs.spriteType ?? 'sprite'"
      label="类型"
      :disabled="attrDisabled"
      :options="spriteTypeOptions"
      @change="onChangeType"
    />
    <AdvancedFeatureMessage
      v-if="androidWatcfaceOS == 'vela' && isVideo"
      :message="'智能类型为轻智能时，无法使用Mp4资源，仅能使用预览图！'"
    />
    <!-- 上传资源 -->
    <template v-for="(spriteItem, index) in spriteItems" :key="spriteItem.id">
      <div
        :class="{
          'mt-2.5': index > 0
        }"
        class="flex items-center gap-x-1"
      >
        <!-- 视频资源 -->
        <template v-if="isVideo">
          <SpriteUploadSectionVideo
            :shape="shape"
            :label="'上传视频' + (maxCount > 1 ? index + 1 : '')"
            :image-label="'上传预览图' + (maxCount > 1 ? index + 1 : '')"
            :sprite-item="spriteItem"
            :sprite-index="index"
          />
        </template>

        <!-- 序列帧图片 -->
        <template v-if="isSprite">
          <SpriteUploadSectionImages
            :shape="shape"
            :label="'上传序列帧' + (maxCount > 1 ? index + 1 : '')"
            :resource-name="spriteItem.spriteImagesName ?? ''"
          />
        </template>

        <!-- 当有多个时，显示删除按钮 -->
        <div v-if="spriteItems.length > 1">
          <icon
            class="cursor-pointer opacity-70 mr-[6px]"
            :class="{
              '!opacity-20': index == 0,
              '!cursor-not-allowed': index == 0
            }"
            icon="delete2.svg"
            :width="14"
            :height="14"
            fill="none"
            @click.stop="
              () =>
                spriteItem.id
                  ? onDel(spriteItem, isSprite ? 'sprite' : 'video')
                  : null
            "
          />
        </div>
      </div>
    </template>
  </AttrGroup>
</template>

<script lang="ts" setup>
import { useWatchfaceAndroid } from '@/hooks';
import { Shape, ShapeType } from '@/services/shape';
import { SpriteAttribute } from '@/services/shape/attributes/sprite';
import { useEditorStore } from '@/store/use-editor-store';
import { canUse } from '@/utils/can-use.utils';
import generateID from '@/utils/generate-id';
import { notification } from 'ant-design-vue';
import { computed } from 'vue';
import { PropType } from 'vue';

const props = defineProps({
  shape: {
    type: Object as PropType<Shape>,
    required: true
  },
  isSprite: {
    type: Boolean,
    required: true
  },
  isVideo: {
    type: Boolean,
    required: true
  },
  maxCount: {
    type: Number,
    default: 1
  },
  spriteItems: {
    type: Array as PropType<SpriteAttribute['spriteExtra']>,
    default: () => []
  }
});

const editorStore = useEditorStore();
const attrDisabled = computed(() => editorStore.attrDisabled);
const { androidWatcfaceOS } = useWatchfaceAndroid();

// 动效类型
const canUseVideo = computed(() => canUse('shape-video').valid);
const spriteTypeOptions = [
  { label: '序列帧', value: 'sprite' },
  { label: 'Mp4视频', value: 'video' }
];

const onChangeType = (v: 'sprite' | 'video') => {
  const shape = props.shape;
  shape.setAttrs({
    spriteType: v,
    spriteExtra: []
  });
};

const onAdd = () => {
  const shape = props.shape;
  const attrs = shape.attrs;
  const maxCount = props.maxCount ?? 1;
  if (props.spriteItems.length >= maxCount) {
    notification.warn({
      message: '数量已达上限',
      description:
        '最多只能添加' + maxCount + '个' + (props.isVideo ? '视频' : '序列帧')
    });
    return;
  }

  // 添加一个视频
  const id = generateID();
  shape.setAttrs({
    spriteExtra: [
      ...(attrs.spriteExtra ?? []),
      {
        videoSrc: '',
        videoName: '',
        videoFps: 0,
        w: attrs.w ?? 0,
        h: attrs.h ?? 0,
        spriteDefaultIndex: 0,
        videoPreviewName: ('videoPreview_' + id) as 'images',
        spriteImagesName: ('images_' + id) as 'images',
        id
      }
    ]
  });
};

const onDel = (
  item: SpriteAttribute['spriteExtra'][0],
  type: 'sprite' | 'video'
) => {
  const id = item.id;
  if (!id) return;

  const shape = props.shape;
  const extra = shape.attrs.spriteExtra ?? [];

  // 如果是序列帧，删除序列帧资源
  if (type == 'sprite') {
    shape.deleteResource({
      resourceName: item.spriteImagesName as 'images'
    });
  }

  // 根据id值删除视频
  shape.setAttrs({
    spriteExtra: extra.filter((_item) => _item.id != item.id)
  });
  if (id == shape.attrs.extraIdToPlay) {
    shape.setAttrs({
      extraIdToPlay: ''
    });
  }
};
</script>
